import moment from 'moment';
import { ChangeEvent, JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as vp from 'vopay';
import { useClientData } from '../../../providers/ClientData.provider';
import { useI18nLabels } from '../../../utils/useI18nLabels';
import { FundRequest } from './types';

export type FundAccountRow = {
  label: string;
  id: vp.FundAccountId;
  require: boolean;
  preset: keyof vp.TextFieldPresets | undefined;
  type: 'input' | 'select' | 'singleDate';
  isReadOnly?: boolean;
  placeholder: string;
  visible: (ctx: FundRequest) => boolean;
  maxLength?: number;
  disableFiltering?: (ctx: FundRequest) => boolean;
  searchCallback?: (term: string) => void;
  onChangeCallBack?: (event: ChangeEvent<HTMLInputElement>, ctx: FundRequest) => void;
  SelectLabel?: (ctx: FundRequest) => string | undefined;
  getSelectOptions: (ctx: FundRequest) => { [key: string]: string | JSX.Element };
};

type BuildFundAccountProps = {
  clientAccount?: vp.ClientAccount;
  moreInfoNeeded: boolean;
};

export const buildFundAccountRows = ({ clientAccount, moreInfoNeeded }: BuildFundAccountProps): FundAccountRow[] => {
  const {
    response: { USDEFTSendEnabled, ClientAccountsEnabled, TransactionLabelOverrideEnabled, Country },
  } = useClientData();

  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.FundMyAccount.Form' });
  const { t: tGlobal } = useTranslation('translations');

  const CurrencyOptions = {
    ...vp.CURRENCY_SELECT_OPTIONS,
  };

  const CADOnly: { [key: string]: string } = {
    CAD: 'CAD',
  };

  const { frequencyLabelMapper } = useI18nLabels();
  const frequencyValues: vp.NameOfFrequency[] = [
    'daily',
    'weekly',
    'bi-weekly',
    'monthly',
    'semi-monthly',
    'quarterly',
    'semi-annually',
    'annually',
    'weekdays',
  ];
  const FrequencyOptions: { [key: string]: string } = Object.fromEntries(
    frequencyValues.map((value) => [value, frequencyLabelMapper(value)]),
  );

  const PaymentOptions: { [key: string]: string | JSX.Element } = {
    'Bank Account': tGlobal('Global.PaymentMethods.bankAccount'),
    'Interac e-Transfer Money Request': <vp.InteracWordmark type="moneyRequest" />,
    'Credit Card': tGlobal('Global.PaymentMethods.creditCard'),
  };

  const BankAccountOption: { [key: string]: string } = {
    'Bank Account': tGlobal('Global.BankAccount.bankAccount'),
  };

  const ScheduleTypeOptions: { [key: string]: string } = {
    single: tGlobal('Global.ScheduleType.single'),
    recurring: tGlobal('Global.ScheduleType.recurring'),
  };

  const EndingOptions: { [key: string]: string } = {
    'End Date': tGlobal('Global.EndingOptions.endDate'),
    Payments: tGlobal('Global.EndingOptions.payments'),
  };

  const SemiMonthlyDateOptions = {
    SemiMonthFirstAndFifteenth: tGlobal('Global.SemiMonthlyDateOptions.firstAndFifteenth'),
    SemiMonthFifteenthAndLast: tGlobal('Global.SemiMonthlyDateOptions.fifteenthAndLast'),
  };

  const clientAccountOptions = useMemo(() => {
    if (clientAccount) {
      return {
        [clientAccount.ClientAccountID]: clientAccount.ClientName,
      };
    }

    return {};
  }, [clientAccount]);

  return [
    {
      label: t('Account.label'),
      id: vp.FundAccountId.Account,
      require: true,
      preset: 'text',
      type: 'input',
      isReadOnly: true,
      placeholder: t('Account.placeholder'),
      visible: (ctx: FundRequest) => ClientAccountsEnabled === '0' || ctx.PaymentMethod === 'Credit Card',
      getSelectOptions: () => ({}),
    },
    {
      label: t('ClientAccount.label'),
      id: vp.FundAccountId.ClientAccount,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: t('ClientAccount.placeholder'),
      isReadOnly: true,
      visible: () => ClientAccountsEnabled === '1',
      getSelectOptions: () => clientAccountOptions,
    },
    {
      label: t('currency'),
      id: vp.FundAccountId.Currency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => true,
      getSelectOptions: () =>
        Country === 'US' ? vp.US_CURRENCY_ONLY : USDEFTSendEnabled === '0' ? CADOnly : CurrencyOptions,
    },
    {
      label: t('paymentMethod'),
      id: vp.FundAccountId.PaymentMethod,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => ClientAccountsEnabled === '1',
      isReadOnly: true,
      getSelectOptions: (ctx: FundRequest) => (ctx.Currency === 'CAD' ? PaymentOptions : BankAccountOption),
    },
    {
      label: t('amount'),
      id: vp.FundAccountId.Amount,
      require: true,
      preset: 'currency',
      type: 'input',
      placeholder: '0.00',
      visible: () => true,
      getSelectOptions: () => ({}),
    },
    {
      label: t('scheduleType'),
      id: vp.FundAccountId.ScheduleType,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: FundRequest) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ScheduleTypeOptions,
    },
    {
      label: t('TakeFromAccountOn.label'),
      id: vp.FundAccountId.TakeFromAccountOn,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: FundRequest) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType !== 'recurring' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('startDate'),
      id: vp.FundAccountId.StartDate,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: FundRequest) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('frequency'),
      id: vp.FundAccountId.Frequency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: FundRequest) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => FrequencyOptions,
    },
    {
      label: t('semiMonthlyDates'),
      id: vp.FundAccountId.SemiMonthlyDates,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: FundRequest) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ctx.Frequency === 'semi-monthly' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => SemiMonthlyDateOptions,
    },
    {
      label: t('ending'),
      id: vp.FundAccountId.Ending,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: FundRequest) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => EndingOptions,
    },
    {
      label: t('endDate'),
      id: vp.FundAccountId.EndDate,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: FundRequest) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ctx.Ending === 'End Date' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('endingAfterPayments'),
      id: vp.FundAccountId.EndingAfterPayments,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: FundRequest) =>
        ctx.Ending === 'Payments' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('Description.label'),
      id: vp.FundAccountId.Description,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: t('Description.placeholder'),
      visible: (ctx: FundRequest) =>
        ctx.PaymentMethod === 'Bank Account' &&
        (ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' &&
            !vp.IsTodayDate(moment(Number(ctx.TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('messageForRecipient'),
      id: vp.FundAccountId.MessageForRecipient,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: FundRequest) => ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      getSelectOptions: () => ({}),
    },
    {
      label: t('transactionLabel'),
      id: vp.FundAccountId.TransactionLabel,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      maxLength: 30,
      visible: (ctx: FundRequest) =>
        TransactionLabelOverrideEnabled === '1' && ctx.PaymentMethod === 'Bank Account' && moreInfoNeeded,
      getSelectOptions: () => ({}),
    },
    {
      label: t('notes'),
      id: vp.FundAccountId.Notes,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: FundRequest) => ctx.PaymentMethod !== 'Credit Card' && moreInfoNeeded,
      getSelectOptions: () => ({}),
    },
  ];
};
